import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LogOut, MapPin, Calendar,
  HardHat, Camera, Inbox, ExternalLink, MessageSquareText, Mail, X, Share2,
  CalendarDays, Users, Contact, KeyRound, Activity, Star, Newspaper,
} from "lucide-react";
import { api, API_BASE, jobPhotoSrc } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { SocialModule } from "../components/social/SocialModule";
import { PeopleModule } from "../components/people/PeopleModule";
import { HeroManager } from "../components/hero/HeroManager";
import { PhotosManager } from "../components/hero/PhotosManager";
import { CalendarModule } from "../components/crew/CalendarModule";
import { OnAssignment } from "../components/crew/OnAssignment";
import { EmployeeAccessTab } from "../components/access/EmployeeAccessTab";
import { SiteFeed } from "../components/feed/SiteFeed";
import { ReviewsTab } from "../components/testimonials/ReviewsTab";
import { BlogManager } from "../components/blog/BlogManager";
import { UploadPanel } from "../components/upload/UploadPanel";
import { todayStr, isActiveOn } from "../components/crew/crewUtils";
import { usePageTitle } from "../hooks/usePageTitle";

export default function Dashboard() {
  usePageTitle("Crew Dashboard");
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState("jobs"); // jobs | feed | bookings | qa | reviews | social | calendar | onassign | people | access
  const [jobs, setJobs] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [qaInbox, setQaInbox] = useState([]);
  const [socialUnreplied, setSocialUnreplied] = useState(null);
  const [reviewsPending, setReviewsPending] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  const isAdmin = user?.role === "admin";

  const refresh = useCallback(async () => {
    try {
      const j = await api.get("/jobs");
      setJobs(j.data || []);
    } catch (e) { console.warn("Dashboard: failed to load jobs", e); }
    if (isAdmin) {
      try {
        const [b, q] = await Promise.all([api.get("/bookings"), api.get("/qa/inbox")]);
        setBookings(b.data || []);
        setQaInbox(q.data || []);
      } catch (e) { console.warn("Dashboard: failed to load bookings/QA inbox", e); }
      try {
        const o = await api.get("/social/overview");
        setSocialUnreplied(o.data?.totals?.unreplied ?? 0);
      } catch (e) { console.warn("Dashboard: failed to load social overview", e); }
      try {
        const t = await api.get("/testimonials/all");
        setReviewsPending((t.data || []).filter((x) => !x.approved).length);
      } catch (e) { console.warn("Dashboard: failed to load reviews", e); }
    }
    try {
      const a = await api.get("/assignments");
      setAssignments(a.data || []);
    } catch (e) { console.warn("Dashboard: failed to load assignments", e); }
  }, [isAdmin]);
  useEffect(() => { refresh(); }, [refresh]);

  const doLogout = async () => { await logout(); navigate("/"); };

  const [reviewReqBusy, setReviewReqBusy] = useState(null);
  const requestReview = async (id) => {
    setReviewReqBusy(id);
    try {
      await api.post(`/bookings/${id}/request-review`);
      refresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not send the review email.");
    } finally {
      setReviewReqBusy(null);
    }
  };

  const activeToday = assignments.filter((a) => isActiveOn(a, todayStr()) && a.status !== "complete").length;

  const counts = {
    jobs: jobs.length,
    bookings: bookings.length,
    qa: qaInbox.length,
    social: socialUnreplied,
    reviews: reviewsPending,
    calendar: null,
    onassign: activeToday,
  };

  return (
    <div className="min-h-screen bg-[#F4F5F6]" data-testid="dashboard-page">
      {/* Top bar */}
      <header className="bg-[#2C3539] text-white border-b-4 border-[#2F5F80] sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <HardHat size={22} className="text-[#2F5F80]" />
            <div className="font-display text-xl tracking-wider uppercase">AV CREW · DASHBOARD</div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-white/70 hover:text-[#2F5F80] text-xs tracking-widest uppercase inline-flex items-center gap-1" data-testid="dashboard-view-site">
              View Site <ExternalLink size={12}/>
            </Link>
            <span className="hidden md:block text-sm text-white/80" data-testid="dashboard-user-email">{user?.email}</span>
            <button onClick={doLogout} data-testid="dashboard-logout" className="inline-flex items-center gap-1 text-xs tracking-widest uppercase border border-white/30 px-3 py-1.5 hover:bg-[#2F5F80] hover:border-[#2F5F80]">
              <LogOut size={14}/> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Sidebar */}
        <aside className="lg:col-span-3">
          <div className="bg-white border-2 border-[#2C3539]/10">
            <SidebarItem id="jobs" label="Photos" icon={Camera} active={tab === "jobs"} onClick={() => setTab("jobs")} count={counts.jobs} />
            {isAdmin && <SidebarItem id="feed" label="Site Feed" icon={Activity} active={tab === "feed"} onClick={() => setTab("feed")} count={null} />}
            {isAdmin && <SidebarItem id="bookings" label="Booking Requests" icon={Inbox} active={tab === "bookings"} onClick={() => setTab("bookings")} count={counts.bookings} />}
            {isAdmin && <SidebarItem id="qa" label="Q&A Inbox" icon={MessageSquareText} active={tab === "qa"} onClick={() => setTab("qa")} count={counts.qa} highlight={counts.qa > 0} />}
            {isAdmin && <SidebarItem id="reviews" label="Reviews" icon={Star} active={tab === "reviews"} onClick={() => setTab("reviews")} count={counts.reviews} highlight={counts.reviews > 0} />}
            {isAdmin && <SidebarItem id="social" label="Social" icon={Share2} active={tab === "social"} onClick={() => setTab("social")} count={counts.social} highlight={counts.social > 0} />}
            {isAdmin && <SidebarItem id="blog" label="AI Blog" icon={Newspaper} active={tab === "blog"} onClick={() => setTab("blog")} count={null} />}
            <SidebarItem id="calendar" label={isAdmin ? "Calendar" : "My Schedule"} icon={CalendarDays} active={tab === "calendar"} onClick={() => setTab("calendar")} count={counts.calendar} />
            {isAdmin && <SidebarItem id="onassign" label="On Assignment" icon={Users} active={tab === "onassign"} onClick={() => setTab("onassign")} count={counts.onassign} highlight={counts.onassign > 0} />}
            {isAdmin && <SidebarItem id="people" label="Demographics" icon={Contact} active={tab === "people"} onClick={() => setTab("people")} count={null} />}
            {isAdmin && <SidebarItem id="access" label="Employee Access" icon={KeyRound} active={tab === "access"} onClick={() => setTab("access")} count={null} />}
          </div>
          <div className="mt-6 bg-[#2C3539] text-white p-5 border-l-4 border-[#2F5F80]">
            <div className="font-display text-xl uppercase">Crew Tip</div>
            <p className="mt-2 text-sm text-white/80 leading-relaxed">Take a few wide shots and one detail shot per job. Always log location, date, and service type so it lands in the right gallery filter.</p>
          </div>
        </aside>

        {/* Main */}
        <main className="lg:col-span-9">
          {tab === "social" && isAdmin && (
            <SocialModule onDataChange={(o) => setSocialUnreplied(o?.totals?.unreplied ?? 0)} />
          )}

          {tab === "calendar" && (
            <CalendarModule assignments={assignments} onRefresh={refresh} readOnly={!isAdmin} />
          )}

          {tab === "onassign" && isAdmin && (
            <OnAssignment assignments={assignments} />
          )}

          {tab === "people" && isAdmin && (
            <PeopleModule />
          )}

          {tab === "access" && isAdmin && (
            <EmployeeAccessTab />
          )}

          {tab === "feed" && isAdmin && (
            <SiteFeed jobs={jobs} onOpenPhoto={(j) => setLightbox({ src: jobPhotoSrc(j), alt: j.job_site_name })} />
          )}

          {tab === "reviews" && isAdmin && (
            <ReviewsTab onCountChange={setReviewsPending} />
          )}

          {tab === "blog" && isAdmin && (
            <BlogManager />
          )}

          {tab === "jobs" && (
            <>
            <UploadPanel onUploaded={refresh} />
            {isAdmin && <HeroManager jobs={jobs} />}
            <PhotosManager jobs={jobs} onRefresh={refresh} isAdmin={isAdmin} />
            </>
          )}


          {tab === "bookings" && isAdmin && (
            <section className="bg-white border-2 border-[#2C3539]/10 p-6 md:p-10" data-testid="bookings-panel">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="font-display text-3xl uppercase text-[#2C3539]">Booking Requests</div>
                  <p className="text-sm text-[#2C3539]/70">Customer enquiries from the public Contact form. Click a photo to enlarge.</p>
                </div>
                <span className="font-mono-av text-xs text-[#2C3539]/60">{bookings.length} REQUEST{bookings.length === 1 ? "" : "S"}</span>
              </div>
              {bookings.length === 0 ? (
                <div className="mt-8 border-2 border-dashed border-[#2C3539]/20 p-10 text-center">
                  <Inbox size={32} className="mx-auto text-[#2F5F80]"/>
                  <div className="mt-3 font-display text-xl uppercase text-[#2C3539]">Inbox is empty</div>
                  <p className="text-sm text-[#2C3539]/60">New booking requests from the public site will land here.</p>
                </div>
              ) : (
                <div className="mt-6 space-y-4">
                  {bookings.map((b) => (
                    <article key={b.id} className="border-2 border-[#2C3539]/10 hover:border-[#2F5F80] transition-colors p-5" data-testid={`booking-${b.id}`}>
                      <div className="flex justify-between flex-wrap gap-2 items-start">
                        <div>
                          <div className="font-display text-xl uppercase text-[#2C3539]">{b.name}</div>
                          <div className="text-xs text-[#2C3539]/60 mt-0.5">{b.created_at?.slice(0,10)} · <span className="bg-[#2F5F80]/10 text-[#2F5F80] px-2 py-0.5 text-[10px] uppercase tracking-wider">{b.service}</span></div>
                        </div>
                        <div className="text-right text-sm">
                          <div><a href={`mailto:${b.email}`} className="text-[#2C3539] hover:text-[#2F5F80]">{b.email}</a></div>
                          <div className="text-[#2C3539]/70"><a href={`tel:${b.phone}`} className="hover:text-[#2F5F80]">{b.phone}</a></div>
                          {b.email_sent ? <span className="text-green-700 text-[10px] uppercase tracking-wider">Email sent</span> : <span className="text-amber-700 text-[10px] uppercase tracking-wider">Saved</span>}
                          <div className="mt-2">
                            {b.review_requested_at ? (
                              <span className="text-[#2F5F80] text-[10px] uppercase tracking-wider font-bold" data-testid={`booking-review-requested-${b.id}`}>
                                ★ Review requested {b.review_requested_at.slice(0, 10)}
                              </span>
                            ) : (
                              <button
                                onClick={() => requestReview(b.id)}
                                disabled={reviewReqBusy === b.id}
                                data-testid={`booking-request-review-${b.id}`}
                                className="inline-flex items-center gap-1 border-2 border-[#2F5F80] text-[#2F5F80] hover:bg-[#2F5F80] hover:text-white text-[10px] font-bold uppercase tracking-[0.1em] px-2.5 py-1.5 transition-colors disabled:opacity-50"
                              >
                                <Star size={11} /> {reviewReqBusy === b.id ? "Sending…" : "Request Review"}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 text-sm text-[#2C3539]/80 whitespace-pre-line">{b.message}</div>
                      {(b.preferred_date || b.location) && (
                        <div className="mt-2 text-xs text-[#2C3539]/60 flex gap-4 flex-wrap">
                          {b.preferred_date && <span><Calendar size={12} className="inline mr-1"/>{b.preferred_date}</span>}
                          {b.location && <span><MapPin size={12} className="inline mr-1"/>{b.location}</span>}
                        </div>
                      )}
                      {b.photo_count > 0 && (
                        <div className="mt-4">
                          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#2F5F80] mb-2">{b.photo_count} attached photo{b.photo_count === 1 ? "" : "s"}</div>
                          <div className="flex gap-2 flex-wrap" data-testid={`booking-photos-${b.id}`}>
                            {Array.from({ length: b.photo_count }).map((_, i) => (
                              <button
                                type="button"
                                key={`${b.id}-photo-${i}`}
                                onClick={() => setLightbox({ src: `${API_BASE}/bookings/${b.id}/photos/${i}`, alt: `Booking ${b.id} photo ${i+1}` })}
                                className="border-2 border-[#2C3539]/15 hover:border-[#2F5F80] block"
                                data-testid={`booking-photo-thumb-${b.id}-${i}`}
                              >
                                <img
                                  src={`${API_BASE}/bookings/${b.id}/photos/${i}`}
                                  alt={`Attached ${i+1}`}
                                  className="w-20 h-20 object-cover"
                                  loading="lazy"
                                />
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </article>
                  ))}
                </div>
              )}
            </section>
          )}

          {tab === "qa" && isAdmin && (
            <section className="bg-white border-2 border-[#2C3539]/10 p-6 md:p-10" data-testid="qa-panel">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="font-display text-3xl uppercase text-[#2C3539]">Q&A Inbox</div>
                  <p className="text-sm text-[#2C3539]/70">Questions the AI flagged for your personal follow-up.</p>
                </div>
                <span className="font-mono-av text-xs text-[#2C3539]/60">{qaInbox.length} ESCALATED</span>
              </div>
              {qaInbox.length === 0 ? (
                <div className="mt-8 border-2 border-dashed border-[#2C3539]/20 p-10 text-center">
                  <MessageSquareText size={32} className="mx-auto text-[#2F5F80]"/>
                  <div className="mt-3 font-display text-xl uppercase text-[#2C3539]">All caught up</div>
                  <p className="text-sm text-[#2C3539]/60">When a visitor asks something the AI can't confidently answer, it'll land here (and be emailed to Char if SMTP is configured).</p>
                </div>
              ) : (
                <div className="mt-6 space-y-4">
                  {qaInbox.map((q) => (
                    <article key={q.id} className="border-2 border-[#2C3539]/10 p-5 hover:border-[#2F5F80] transition-colors" data-testid={`qa-item-${q.id}`}>
                      <div className="flex justify-between flex-wrap gap-2">
                        <div className="font-display text-lg uppercase text-[#2C3539]">{q.name || "Anonymous"}</div>
                        <div className="text-xs text-[#2C3539]/60">{q.created_at?.slice(0, 10)}</div>
                      </div>
                      {q.email && (
                        <a href={`mailto:${q.email}`} className="text-xs text-[#2C3539]/70 hover:text-[#2F5F80] inline-flex items-center gap-1 mt-1">
                          <Mail size={12}/> {q.email}
                        </a>
                      )}
                      <div className="mt-3 text-sm text-[#2C3539] whitespace-pre-line">
                        <span className="font-bold text-[#2F5F80] uppercase tracking-wider text-[10px] block mb-1">Question</span>
                        {q.question}
                      </div>
                      <div className="mt-3 bg-[#F4F5F6] p-3 text-xs text-[#2C3539]/70 italic">
                        AI reasoning: {q.reasoning || "—"}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          )}
        </main>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/85 z-[60] flex items-center justify-center p-5"
          onClick={() => setLightbox(null)}
          data-testid="photo-lightbox"
        >
          <button onClick={() => setLightbox(null)} className="absolute top-5 right-5 text-white hover:text-[#2F5F80]" aria-label="Close">
            <X size={28}/>
          </button>
          <img src={lightbox.src} alt={lightbox.alt} className="max-w-full max-h-[90vh] object-contain"/>
        </div>
      )}
    </div>
  );
}

function SidebarItem({ id, label, icon: Icon, active, onClick, count, highlight }) {
  return (
    <button
      onClick={onClick}
      data-testid={`sidebar-${id}`}
      className={`w-full flex items-center justify-between px-5 py-4 border-l-4 text-sm uppercase tracking-[0.18em] font-display transition-colors ${
        active
          ? "bg-[#2C3539] text-white border-[#2F5F80]"
          : "bg-white text-[#2C3539] border-transparent hover:bg-[#F4F5F6]"
      }`}
    >
      <span className="inline-flex items-center gap-3"><Icon size={18}/> {label}</span>
      {count != null && (
        <span className={`font-mono-av text-xs ${highlight && !active ? "bg-[#2F5F80] text-white px-2 py-0.5" : "opacity-70"}`}>{count}</span>
      )}
    </button>
  );
}
